import React, {
  type MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { useMetrics } from "@/api/ads";
import Scorecard from "@/components/scorecard";
import StatusMedia from "@/components/status-media";
import { Stack } from "@/components/ui";
import AccountContainer from "@/containers/account-container";
import { useAuth } from "@/contexts/auth/useAuth";
import PredictionCard from "@/features/predictions/components/prediction/prediction-card";
import NewsEmbedCard from "@/features/sports-news/news-embed-card";
import EmbeddedAnnouncement from "@/features/status/components/embedded-announcement";
import { cn } from "@/utils/cn";
import { defaultMediaVisibility } from "@/utils/status";

import OutlineBox from "./outline-box";
import StatusContent from "./status-content";
import StatusReplyMentions from "./status-reply-mentions";
import GroupHeader from "./statuses/header/group";
import SensitiveContentOverlay from "./statuses/sensitive-content-overlay";

import type { PredictionStatus, Status } from "@/schemas";

interface QuotedStatusProps {
  /** The quoted status entity. */
  status: Status;
  /** Whether the status is shown in the post composer. */
  compose?: boolean;
  source?: string | undefined;
  avatarSize?: number;
}

/** Status embedded in a quote post. */
const QuotedStatus: React.FC<QuotedStatusProps> = ({
  status,
  compose,
  source,
  avatarSize = 42,
}) => {
  const { currentAccount } = useAuth();
  const navigate = useNavigate();

  const displayMedia = currentAccount?.settings?.displayMedia as string;

  const overlay = useRef<HTMLDivElement>(null);

  const [showMedia, setShowMedia] = useState<boolean>(
    defaultMediaVisibility(status, displayMedia),
  );
  const [minHeight, setMinHeight] = useState(208);

  void useMetrics(status?.metrics, source);

  useEffect(() => {
    if (overlay.current) {
      setMinHeight(overlay.current.getBoundingClientRect().height);
    }
  }, [overlay.current]);

  const handleExpandClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (!status) return;
    const account = status.account;

    if (!compose && e.button === 0) {
      const statusUrl = `/@${account.acct}/posts/${status.id}`;
      if (!(e.ctrlKey || e.metaKey)) {
        navigate(statusUrl);
      } else {
        window.open(statusUrl, "_blank");
      }
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleToggleMediaVisibility = () => {
    setShowMedia(!showMedia);
  };

  const renderContent = () => {
    return (
      <Stack space={4} onClick={handleExpandClick}>
        {status.group ? (
          <GroupHeader
            status={status as any}
            group={status.group}
            account={status.account}
            timestamp={status.created_at}
            avatarSize={avatarSize}
          />
        ) : (
          <AccountContainer
            hideActions
            id={status.account.id}
            timestamp={status.created_at}
            withRelationship={false}
            showProfileHoverCard={!compose}
            withLinkToProfile={!compose}
          />
        )}

        <StatusReplyMentions status={status} hoverable={false} />

        <Stack
          className="relative z-0"
          style={{
            minHeight: status.hidden
              ? Math.max(minHeight, 208) + 12
              : undefined,
          }}
        >
          {status.hidden && (
            <SensitiveContentOverlay
              status={status}
              visible={showMedia}
              onToggleVisibility={handleToggleMediaVisibility}
              ref={overlay}
            />
          )}

          <Stack space={4}>
            <StatusContent status={status} collapsable />

            {(status.media_attachments.length > 0 || status.card) && (
              <StatusMedia
                status={status}
                muted={compose}
                showMedia={showMedia}
                onToggleVisibility={handleToggleMediaVisibility}
              />
            )}

            {status.news_article && (
              <NewsEmbedCard article={status.news_article} />
            )}
            {status.sports_game && (
              <Scorecard variant="embed" event={status.sports_game} />
            )}
            {status.prediction && (
              <PredictionCard
                status={status as unknown as PredictionStatus}
                isQuoted
              />
            )}
          </Stack>
        </Stack>

        {status.embedded_announcement && (
          <EmbeddedAnnouncement announcement={status.embedded_announcement} />
        )}
      </Stack>
    );
  };

  return (
    <OutlineBox
      data-testid="quoted-status"
      className={cn("cursor-pointer", {
        "hover:bg-gray-100 dark:hover:bg-gray-800": !compose,
      })}
    >
      {status.prediction ? (
        <PredictionCard
          status={status as unknown as PredictionStatus}
          isQuoted
        />
      ) : (
        renderContent()
      )}
    </OutlineBox>
  );
};

export default QuotedStatus;
